import { c as _c } from "react/compiler-runtime";
import { Prose } from '@/components/Atoms/Prose/Prose';
import { BVideoPlayer, IdType } from '@/components/Molecules/BVideoPlayer/BVideoPlayer';
import type { IBreadcrumbItem } from '@/components/Molecules/Breadcrumb/Breadcrumb';
import { HeadMeta } from '@/components/Molecules/HeadMeta/HeadMeta';
import { ImageWithSource } from '@/components/Molecules/ImageWithSource/ImageWithSource';
import { RelatedSidebar, sidebarContentCardRenderer } from '@/components/Molecules/RelatedSidebar/RelatedSidebar';
import { ArticlePageHeader } from '@/components/Organisms/ArticlePageHeader/ArticlePageHeader';
import { ContentItemCard } from '@/components/Organisms/ContentItemCard/ContentItemCard';
import { ContentTranslation } from '@/components/Organisms/ContentTranslation/ContentTranslation';
import { MachineTranslationDisclaimer } from '@/components/Organisms/ContentTranslation/MachineTranslationDisclaimer';
import { Contributors } from '@/components/Organisms/Contributors/Contributors';
import { EmbeddedVideo } from '@/components/Organisms/EmbeddedVideo/EmbeddedVideo';
import { ModelInstructions } from '@/components/Organisms/ModelInstructions/ModelInstructions';
import { PolicyCard } from '@/components/Organisms/PolicyCard/PolicyCard';
import { ScheduledEvent } from '@/components/Organisms/ScheduledEvent/ScheduledEvent';
import { ByTheNumbersPanel } from '@/components/Pages/Insights/ByTheNumbersPanel';
import { ArticlePageAside, ArticlePageMain, ArticlePageTemplate } from '@/components/Templates/ArticlePageTemplate/ArticlePageTemplate';
import { InfiniteScrollPage } from '@/components/Templates/InfiniteScrollPage/InfiniteScrollPage';
import type { IPageItemFetcher, IPageRendererProps } from '@/components/Templates/InfiniteScrollPage/ItemPageRenderer';
import { SailthruTracking } from '@/hooks/useSailthruTracking';
import useTranslation from '@/hooks/useTranslation';
import { useUserContentViewTracker } from '@/hooks/useUserContentViewTracker';
import type { IContentItem, IEvent, IFormChoice, IInsightNoteDetail, IModelDetail, IPerson, IPolicySummary } from '@/interfaces';
import { ContentType } from '@/interfaces';
import { TrackedProductTypes, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import { getAllResearchQuery, SearchContentType } from '@/services/Search';
import classNames from '@/utils/classNames';
import { adjustImageAsset } from '@/utils/imageUtils';
import type { ShareAction } from 'bnef';
import type { Translate } from 'next-translate';
import { useRouter } from 'next/router';
import type { FC } from 'react';
import { useId } from 'react';
import styles from './InsightExecSumPage.module.css';
import type { AttachmentWithAllowance, KeyedAttachments } from './useInsightExecSumActions';
import { ActionWithDialog, getReportAction, useInsightExecSumActions } from './useInsightExecSumActions';
export interface IInsightNoteItemProps {
  insight: IInsightNoteDetail;
  attachmentsWithAllowance: AttachmentWithAllowance[];
  isDataset: boolean;
  eventsUpcoming: IEvent[];
  eventsOnDemand: IContentItem[];
  contributors: IPerson[];
  relatedInsights: IContentItem[];
  shareToken: null | ShareAction;
  watermark: string;
  model?: IModelDetail;
  relatedPolicies: IPolicySummary[];
  relatedInteractiveDatasets: IContentItem[];
  translationLocales?: IFormChoice[];
}
export const insightsBreadcrumbs = (t: Translate): IBreadcrumbItem[] => [{
  url: '/insights',
  label: t('common:Sections.insights')
}];
const resultsBuilder = async (initialItem: IContentItem, query?: Record<string, any>): Promise<IContentItem[]> => {
  const resultItems: IContentItem[] = [];
  const searchResults = await new BrowserHttpClient().search({
    path: '/research_search',
    body: getAllResearchQuery(SearchContentType.Insight, query)
  });
  if (searchResults?.items) {
    searchResults.items.forEach((item: IContentItem) => {
      if (item.id.toString() !== initialItem.id) {
        resultItems.push(item);
      }
    });
  }
  return resultItems;
};
export const InsightPageAnalyticsInfo = {
  pageName: 'BNEF: Insights: Detail View',
  siteSection: 'BNEF: Insights',
  type: TrackedProductTypes.Insight
};
export const insightPageFetcher: IPageItemFetcher<IInsightNoteItemProps> = async (id: string) => {
  const pageData = await new BrowserHttpClient().get({
    path: '/insights/[ID]',
    replacements: {
      id
    }
  });
  if (pageData?.insight) {
    SailthruTracking.saveTags(id, pageData.insight.type, pageData.insight.tags);
    return {
      pageData: pageData as IInsightNoteItemProps
    };
  }
  return null;
};
export const InsightPageRenderer = ({
  pageData,
  isActive
}: IPageRendererProps<IInsightNoteItemProps>) => {
  useUserContentViewTracker(pageData.insight.id, ContentType.insight, isActive);
  const {
    t
  } = useTranslation('insights');
  const router = useRouter();
  const lang = router.query?.language as string;
  const {
    insight
  } = pageData;
  const disclaimerId = useId();
  const relatedItems = [...pageData.relatedInsights, ...pageData.relatedInteractiveDatasets];
  const seriesBackgroundImage = insight.parentSeries?.leadImage?.baseUrl ? adjustImageAsset(insight.parentSeries.leadImage.baseUrl, 1920) : undefined;
  let contentType = 'insight';
  if (pageData.isDataset) {
    contentType = 'dataset';
  } else if (pageData.model) {
    contentType = 'model';
  }
  const isModel = Boolean(pageData.model);
  const isDataset = pageData.isDataset;
  const attachmentsByType: KeyedAttachments = {};
  pageData.attachmentsWithAllowance.forEach(attachment => {
    attachmentsByType[attachment.type] = attachment;
  });
  const actions = useInsightExecSumActions({
    insight,
    shareToken: pageData.shareToken,
    isModel,
    isDataset,
    attachmentsByType,
    watermark: pageData.watermark
  });
  let reportLink;
  if (!isModel && !isDataset && attachmentsByType.pdf) {
    reportLink = <ActionWithDialog kind="tertiary" action={getReportAction(insight, attachmentsByType.pdf, t, lang)} />;
  }
  return <div className={classNames({
    [styles.pageBackground]: !!seriesBackgroundImage
  })} style={seriesBackgroundImage ? {
    ['--_background' as string]: `url(${seriesBackgroundImage})`
  } : undefined}>
            <ArticlePageHeader title={insight.title} breadCrumbs={insightsBreadcrumbs(t)} date={insight.displayDate} asUnpublished={insight.isPublished === false} prefix={insight.parentSeries ? <a href={insight.parentSeries.url}>{insight.parentSeries.title}</a> : undefined} actions={actions} asBanner={!!seriesBackgroundImage} />
            <div className={styles.mainArticle}>
                <ArticlePageTemplate>
                    <ArticlePageMain>
                        <ContentTranslation originalContentLanguage="en" targetDestination={pageData.insight.url} currentTranslationDetails={pageData.insight?.translationDetails ?? null} languageOptions={pageData.translationLocales ?? []} disclaimerId={disclaimerId} />
                        <Prose $preformatted>
                            <p className={classNames('cds-t-group-heading2', styles.typeLabel)}>
                                {t(`insights:InsightExecSumPage.ContentType.${contentType}`)}
                            </p>
                            <p>{insight.abstract}</p>
                        </Prose>
                        {insight.embeddedVideo && <EmbeddedVideo video={insight.embeddedVideo} as="h2" />}
                        {insight.mrcVideoId && <BVideoPlayer videoId={insight.mrcVideoId} videoIdType={IdType.BMMR} />}
                        {insight.byTheNumbers.length > 0 && <ByTheNumbersPanel items={insight.byTheNumbers} />}
                        {insight.chart && <ImageWithSource title={insight.chart.title} publicationSource={insight.chart.source} publicationNote={insight.chart.note} image={insight.leadImage} imageToasterData={insight.chart.toasterData} />}
                        {insight.bulletPoints.length > 0 && <Prose $preformatted>
                                <ul>
                                    {insight.bulletPoints.map((el, i) => <li key={i}>{el}</li>)}
                                </ul>
                            </Prose>}
                        {pageData.model && <ModelInstructions model={pageData.model} />}
                        {reportLink}
                        <MachineTranslationDisclaimer id={disclaimerId} translationDetails={pageData.insight?.translationDetails} />
                    </ArticlePageMain>
                    <ArticlePageAside>
                        <RelatedSidebar lists={[(pageData.eventsOnDemand.length > 0 || pageData.eventsUpcoming.length > 0) && {
            title: t('common:SiteSection.events'),
            items: [...pageData.eventsUpcoming, ...pageData.eventsOnDemand],
            itemRenderer: (item: IContentItem | IEvent) => {
              if ('eventDate' in item) {
                return <ScheduledEvent initialHeadingLevel={2} event={item as IEvent} boxed />;
              }
              return <ContentItemCard item={item as IContentItem} headingLevel="h3" analyticsCategory={TrackingCategory.RelatedContent} analyticsBaseId={`${item.type}-${item.id}`} responsive={false} />;
            }
          }, relatedItems.length > 0 && {
            title: t('common:Sections.related'),
            items: relatedItems,
            itemRenderer: sidebarContentCardRenderer(`Related: Curated: Insights ${insight.id}`)
          }, pageData.relatedPolicies.length > 0 && {
            title: t('common:Policies.panelHeadings.relatedPolicies'),
            items: pageData.relatedPolicies,
            itemRenderer: (item_0: IPolicySummary) => <PolicyCard item={item_0} />
          }]} />

                        {pageData.contributors.length > 0 && <Contributors items={pageData.contributors} />}
                    </ArticlePageAside>
                </ArticlePageTemplate>
            </div>
        </div>;
};
export const InsightExecSumPage: FC<IInsightNoteItemProps> = props => {
  const $ = _c(9);
  let t0;
  if ($[0] !== props.insight.leadImage.baseUrl || $[1] !== props.insight.tags || $[2] !== props.insight.title) {
    t0 = <HeadMeta title={props.insight.title} type={ContentType.insight} image={props.insight.leadImage.baseUrl} tags={props.insight.tags} />;
    $[0] = props.insight.leadImage.baseUrl;
    $[1] = props.insight.tags;
    $[2] = props.insight.title;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== props) {
    t1 = <InfiniteScrollPage initialPageData={props} resultsBuilder={resultsBuilder} currentItem={props.insight} />;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t0 || $[7] !== t1) {
    t2 = <>{t0}{t1}</>;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};